import { useEffect, useCallback, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { usePrevious } from '@wetransfer/transfer/hooks/usePrevious';
import Analytics from '@wetransfer/transfer/modules/analytics';

/* Redux */
import Actions from '@wetransfer/transfer/store/action';
import { accountSelector } from '@wetransfer/transfer/store/selector/account';
import { channelSelector } from '@wetransfer/transfer/store/selector/channel';
import { selectNotification } from '@wetransfer/transfer/store/selector/notification';
import { selectSession } from '@wetransfer/transfer/store/selector/session';
import {
  selectTransfer,
  selectTransferState,
} from '@wetransfer/transfer/store/selector/transfer';
import { allowedSizeSelector } from '@wetransfer/transfer/store/selector/transfers';
import { uiSelector } from '@wetransfer/transfer/store/selector/ui';
import { selectUser } from '@wetransfer/transfer/store/selector/user';
import {
  hasStarted,
  validateTransfer,
} from '@wetransfer/transfer/transfer-bundle/helpers';
import * as TransferActions from '@wetransfer/transfer/transfer-bundle/store/actions';
import {
  getCurrentSize,
  getIsForwardedTransfer,
} from '@wetransfer/transfer/transfer-bundle/store/selectors';

export const useUploaderFormDispatchers = () => {
  const dispatch = useDispatch();

  const createTransfer = useCallback(
    () => dispatch(Actions.Transfer.create),
    [dispatch]
  );

  const clearTransferNotification = useCallback(
    () => dispatch(Actions.Notification.clearTransfer()),
    [dispatch]
  );

  const pushTransferNotification = useCallback(
    (notification) => dispatch(Actions.Notification.pushTransfer(notification)),
    [dispatch]
  );

  const changeTransferType = useCallback(
    (type) => dispatch(Actions.Transfer.changeType(type)),
    [dispatch]
  );

  const setIsOptionsVisible = useCallback(
    (bool) => dispatch(Actions.UI.setUploaderOptions({ visible: bool })),
    [dispatch]
  );

  return {
    createTransfer,
    clearTransferNotification,
    pushTransferNotification,
    changeTransferType,
    setIsOptionsVisible,
  };
};

export const useIsDisabled = () => {
  const [isDisabled, setIsDisabled] = useState(true);

  const session = useSelector(selectSession);
  const transfer = useSelector(selectTransfer);

  useEffect(() => {
    const disabledBase =
      !session.tandc ||
      hasStarted(transfer.state, transfer.id) ||
      !transfer.files.length;

    switch (transfer.type) {
      case 'link':
        return setIsDisabled(disabledBase);
      case 'email':
        return setIsDisabled(
          disabledBase || !transfer.recipients.length || !transfer.sender.length
        );
      default:
        return setIsDisabled(disabledBase);
    }
  }, [
    session.tandc,
    transfer.state,
    transfer.id,
    transfer.files,
    transfer.recipients,
    transfer.sender,
    transfer.type,
    setIsDisabled,
  ]);

  return [isDisabled, setIsDisabled];
};

export const useScrollable = () => {
  const scrollableRef = useRef(null);
  const ui = useSelector(uiSelector);
  const transfer = useSelector(selectTransfer);
  const prevUI = usePrevious(ui);
  const prevTransfer = usePrevious(transfer);

  const scrollTo = useCallback(
    (...args) => {
      if (scrollableRef.current) {
        scrollableRef.current.scrollTo(...args);
      }
    },
    [scrollableRef]
  );

  const getScrollPosition = () => {
    return scrollableRef.current.getScrollPosition();
  };

  useEffect(() => {
    if (!prevUI?.uploader.options.visible && ui.uploader.options.visible) {
      scrollTo('bottom', {
        animated: true,
        duration: 400,
        blockEvents: true,
      });
    }
  }, [prevUI, ui, scrollTo]);

  useEffect(() => {
    if (prevTransfer?.type !== transfer.type) {
      scrollTo('bottom');
    }
  }, [prevTransfer, transfer, scrollTo]);

  return { scrollableRef, scrollTo, getScrollPosition };
};

export const useCreateTransfer = () => {
  const { createTransfer } = useUploaderFormDispatchers();
  const transferState = useSelector(selectTransferState);

  useEffect(() => {
    if (transferState === 'downloadable') {
      createTransfer();
    }
  }, [createTransfer, transferState]);
};

export const useUploadTransfer = (transferInitialized) => {
  const prevTransferInitialized = usePrevious(transferInitialized);
  const dispatch = useDispatch();
  const { clearTransferNotification } = useUploaderFormDispatchers();
  const account = useSelector(accountSelector);
  const allowedSize = useSelector(allowedSizeSelector);
  const channel = useSelector(channelSelector);
  const currentSize = useSelector(getCurrentSize);
  const isForwardedTransfer = useSelector(getIsForwardedTransfer);
  const notification = useSelector(selectNotification);
  const session = useSelector(selectSession);
  const transfer = useSelector(selectTransfer);
  const user = useSelector(selectUser);

  const uploadTransfer = useCallback(
    () =>
      dispatch(
        TransferActions.uploadTransfer({
          channel,
          account,
          user,
          session,
          notification,
          allowedSize,
          isForwardedTransfer,
          currentSize,
        })
      ),
    [
      account,
      allowedSize,
      channel,
      currentSize,
      dispatch,
      isForwardedTransfer,
      notification,
      session,
      user,
    ]
  );

  const trackTransfer = useCallback(() => {
    const tracker = user.loggedIn
      ? 'WeTransfer_Site_Landing_Page_Overlay_WT_plus_send_file_Conversion'
      : 'WeTransfer_Site_Landing_Page_Overlay_WT_Free_send_file_Conversion';
    Analytics.trackWithPixelOrSDK(tracker);
    Analytics.trackSnowplowEvent({
      category: 'transfer',
      action: 'click_transfer',
      label: `transfer-${
        transfer.password ? 'with' : 'without'
      }-password-expiryDate-${transfer.expiry}`,
    });
  }, [transfer.expiry, transfer.password, user.loggedIn]);

  useEffect(() => {
    if (transferInitialized & !prevTransferInitialized) {
      clearTransferNotification();
      trackTransfer();
      uploadTransfer();
    }
  }, [
    transferInitialized,
    prevTransferInitialized,
    clearTransferNotification,
    uploadTransfer,
    trackTransfer,
  ]);
};

export const useValidateTransfer = (setIsDisabled) => {
  const allowedSize = useSelector(allowedSizeSelector);
  const transfer = useSelector(selectTransfer);
  const { rules } = useSelector(selectUser);
  const { maximumNumberOfFiles, maximumNumberOfRecipients } = rules;

  useEffect(() => {
    if (transfer.files.length) {
      try {
        validateTransfer({
          transfer,
          maximumNumberOfRecipients,
          maximumNumberOfFiles,
          allowedSize,
          currentSize: getCurrentSize({ transfer }),
        });
      } catch (e) {
        setIsDisabled(true);
      }
    }
  }, [
    allowedSize,
    setIsDisabled,
    transfer,
    maximumNumberOfFiles,
    maximumNumberOfRecipients,
  ]);
};
